/**
 * User Display Utilities
 * Helper functions for displaying user information with Plus member highlighting
 */

/**
 * Check if a user is an Admin member (kurucu-admin)
 * @param {Object} user - User object
 * @returns {boolean} - True if user is Admin member
 */
export const isAdminMember = (user) => {
  if (!user) return false;

  // If backend explicitly set isPlus, use it as the source of truth.
  // Group roles ('owner'/'admin'/'member') must not trigger global admin detection.
  if (user.isPlus === false) return false;

  const role = (user.role || user.membership_type || user.membershipType || '').toString().trim().toLowerCase();
  return role === 'admin';
};

/**
 * Check if a user is a Plus member
 * @param {Object} user - User object
 * @returns {boolean} - True if user is Plus member
 */
export const isPlusMember = (user) => {
  if (!user) return false;

  // Backend sets isPlus explicitly — trust it as the single source of truth.
  // This prevents group roles ('owner'/'admin'/'member') from being mistaken
  // for global roles ('plus'/'admin') and triggering wrong colors/badges.
  if (user.isPlus === true || user.isPlus === 1) return true;
  if (user.isPlus === false) return false; // backend explicitly says not Plus → stop here

  // isPlus field absent (legacy objects without explicit flag) — fall back to role checks
  if (user.isPlusUser === true || user.is_plus_user === true || user.isPlusUser === 1 || user.is_plus_user === 1) {
    return true;
  }

  const role = (user.role || user.membership_type || user.membershipType || '').toString().trim().toLowerCase();
  const membershipType = (user.membershipType || user.membership_type || role || '').toString().trim().toLowerCase();

  // Global admin is also Plus (only reached when isPlus field is absent)
  if (role === 'admin') return true;

  // Check if role or membershipType is 'plus'
  if (role === 'plus' || membershipType === 'plus') {
    return true;
  }
  
  // Check plusExpiryDate - if exists and is a valid date, consider user as Plus
  // This works offline too - if user has an expiry date, they're Plus regardless of online/offline status
  const expiryDate = user.plusExpiryDate || user.plus_expiry_date;
  if (expiryDate) {
    try {
      const expiry = new Date(expiryDate);
      // Check if date is valid
      if (isNaN(expiry.getTime())) {
        // Invalid date, skip this check
        return false;
      }
      // If expiry date exists and is valid, user is Plus
      // For offline mode: if expiry date exists (even if in past), we still consider them Plus
      // This ensures Plus users stay highlighted even when offline
      // User requested: "sürekli sarı olması lazım çevrimiçi-çevrimdışı fark etmez"
      return true; // If expiry date exists and is valid, user is Plus (works offline)
    } catch (error) {
      // Date parsing failed, skip this check
      console.warn('Failed to parse plusExpiryDate:', expiryDate, error);
      return false;
    }
  }
  
  return false;
};

/**
 * Get text color classes for Plus members
 * @param {Object} user - User object
 * @returns {string} - Tailwind CSS classes for Plus member text color
 */
export const getPlusTextColor = (user) => {
  if (isPlusMember(user)) {
    return 'text-plus dark:text-plus-dark';
  }
  return '';
};

/**
 * Get text color classes for Plus members (for names)
 * @param {Object} user - User object
 * @returns {string} - Tailwind CSS classes for Plus member name color
 */
export const getPlusNameColor = (user) => {
  if (isPlusMember(user)) {
    return 'text-plus dark:text-plus-dark font-semibold';
  }
  return 'text-gray-900 dark:text-white';
};

/**
 * Get text color classes for Plus members (for emails)
 * @param {Object} user - User object
 * @returns {string} - Tailwind CSS classes for Plus member email color
 */
export const getPlusEmailColor = (user) => {
  if (isPlusMember(user)) {
    return 'text-plus dark:text-plus-dark';
  }
  return 'text-gray-600 dark:text-gray-400';
};

/**
 * Get text color classes for Plus members (for usernames)
 * @param {Object} user - User object
 * @returns {string} - Tailwind CSS classes for Plus member username color
 */
export const getPlusUsernameColor = (user) => {
  if (isPlusMember(user)) {
    return 'text-plus dark:text-plus-dark';
  }
  return 'text-gray-500 dark:text-gray-400';
};

/**
 * GLOBAL USERNAME COLOR UTILITY
 * Returns CSS classes for username display based on Plus membership status.
 * Default: white/gray (normal users)
 * Plus: Yellow/gold (Plus members)
 * 
 * This is the SINGLE SOURCE OF TRUTH for username colors across the entire app.
 * Use this function everywhere instead of hardcoded colors.
 * 
 * @param {Object} user - User object
 * @param {Object} options - Optional configuration
 * @param {string} options.variant - 'default' | 'bold' | 'subtle' | 'gradient'
 * @param {boolean} options.includeFontWeight - Include font-weight classes (default: true)
 * @returns {string} - CSS classes for username color
 */
export const getUsernameColorClasses = (user, options = {}) => {
  const { variant = 'default', includeFontWeight = true } = options;
  const isPlus = isPlusMember(user);
  
  // Plus members: Always yellow/gold
  if (isPlus) {
    const fontWeight = includeFontWeight ? 'font-bold' : '';
    
    switch (variant) {
      case 'gradient':
        // For large headings (like ProfilePage)
        return `bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 dark:from-amber-400 dark:via-yellow-300 dark:to-amber-400 bg-clip-text text-transparent ${fontWeight}`.trim();
      case 'bold':
        return `text-plus dark:text-plus-dark ${fontWeight}`.trim();
      case 'subtle':
        return `text-plus dark:text-plus-dark font-medium`.trim();
      default:
        // Default: Use data attribute + inline style approach (handled by Username component)
        // But also provide classes as fallback
        return `username-plus-member ${fontWeight}`.trim();
    }
  }
  
  // Non-Plus members: Default white/gray
  const fontWeight = includeFontWeight 
    ? (variant === 'bold' ? 'font-bold' : variant === 'subtle' ? 'font-medium' : 'font-semibold')
    : '';
  
  switch (variant) {
    case 'gradient':
      return `bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent ${fontWeight}`.trim();
    case 'bold':
      return `text-gray-900 dark:text-white ${fontWeight}`.trim();
    case 'subtle':
      return `text-gray-600 dark:text-gray-400 ${fontWeight}`.trim();
    default:
      return `text-gray-900 dark:text-white ${fontWeight}`.trim();
  }
};
